import React, { useMemo, useReducer } from 'react';
import { Box } from '@material-ui/core';
import { connect } from 'react-redux';

import LaunchesHeader from '../LaunchHeader';
import LaunchesTable from './LaunchesTable';
import { useStyles as useCommonStyles } from '../LaunchProducts.style';

import PrimaryButton from '../../../components/button/Primary';
import SecondaryButton from '../../../components/button/Secondary';
import DefaultButton from '../../../components/button/Default';
import SearchBox from './SearchBox';
import { useHistory } from 'react-router-dom';
import NoLaunches from './NoLaunches';
import { getTime, addHours } from 'date-fns';

const defaultState = {
    searchTerm: '',
    showExpiredLaunches: false,
};

const launchesViewReducer = (state = defaultState, { type, payload }) => {
    switch (type) {
        case 'UPDATE_SEARCH':
            return { ...state, searchTerm: payload };
        case 'CLEAR_SEARCH':
            return { ...state, searchTerm: '' };
        case 'TOGGLE_EXPIRED_LAUNCHES':
            return { ...state, showExpiredLaunches: !state.showExpiredLaunches };
        default:
            return state;
    }
};

const LaunchesView = ({ launches, currentUser }) => {
    const commonClasses = useCommonStyles();

    const [state, dispatch] = useReducer(launchesViewReducer, defaultState);
    const { searchTerm, showExpiredLaunches } = state;

    const history = useHistory();

    const addLaunch = () => history.push('/launches/new');
    const editLaunch = (launchID) => history.push(`/launches/${launchID}`);

    const handleSearchChange = (e) => {
        dispatch({
            type: 'UPDATE_SEARCH',
            payload: e.target.value,
        });
    };

    const filteredLaunches = useMemo(() => {
        return launches
            .filter(({ endDate, launchDate }) => {
                if (showExpiredLaunches) return true;
                if (!endDate) endDate = getTime(addHours(launchDate, 48));
                return endDate > Date.now();
            })
            .filter(({ PLU, name }) => {
                if (!searchTerm) return true;
                return [PLU, name]
                    .map((field) => field.toString().toLowerCase())
                    .some((field) => field.includes(searchTerm.toLowerCase()));
            })
            .sort((a, b) => b.launchDate - a.launchDate);
    }, [launches, searchTerm, showExpiredLaunches]);

    const ExpiredLaunchesButton = () => {
        const ButtonComponent = showExpiredLaunches ? PrimaryButton : SecondaryButton;
        const onClick = () => dispatch({ type: 'TOGGLE_EXPIRED_LAUNCHES' });
        return (
            <ButtonComponent onClick={onClick}>
                {showExpiredLaunches ? 'Hide' : 'Show'} Expired Launches
            </ButtonComponent>
        );
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="spaceBetween" height="100%">
            <LaunchesHeader
                leftItems={[
                    <DefaultButton onClick={history.goBack} key="back">
                        Go Back
                    </DefaultButton>,
                    <SearchBox
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={commonClasses.searchInput}
                        key="searchbox"
                    />,
                ]}
                rightItems={
                    currentUser.permissions.includes('launches--add')
                        ? [
                              <ExpiredLaunchesButton key="expired-launches" />,
                              <PrimaryButton key="add-launch" onClick={addLaunch}>
                                  Add Launch
                              </PrimaryButton>,
                          ]
                        : [<ExpiredLaunchesButton key="expired-launches" />]
                }
            />
            <Box flex={1} mx={6} display="flex" flexDirection="column" justifyContent="baseline" alignItems="center">
                {filteredLaunches.length === 0 ? (
                    <NoLaunches onAddLaunch={addLaunch} />
                ) : (
                    <LaunchesTable launches={filteredLaunches} onEdit={editLaunch} currentUser={currentUser} />
                )}
            </Box>
        </Box>
    );
};

function mapStateToProps(state) {
    return {
        currentUser: state.auth.currentUser,
    };
}

export default connect(mapStateToProps)(LaunchesView);
